import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  getCallTypes, 
  getCallTypeParameters, 
  getTenantProcessConfig, 
  updateTenantProcessConfig 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Settings, Phone, Voicemail, FileCheck, UserCheck, Calendar, 
  GraduationCap, Gift, ClipboardCheck, Clock, Users,
  MessageCircle, HelpCircle, Save, RefreshCw, Info,
  CheckCircle, XCircle, Briefcase, Star, TrendingUp,
  Handshake, DollarSign, Building, UserPlus
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Call type icon mapping
const CALL_TYPE_ICONS = {
  job_outreach: Phone,
  voicemail: Voicemail,
  submission_call: FileCheck,
  pre_screening: UserCheck,
  spotlight_call: Star,
  interview_request: Calendar,
  interview_prep: GraduationCap,
  interview_debrief: MessageCircle,
  job_offer: Gift,
  counteroffer: Handshake,
  salary_negotiation: DollarSign,
  onboarding: ClipboardCheck,
  placement_checkin: TrendingUp,
  timesheet: Clock,
  referral_check: Users,
  client_introduction: Building,
  follow_up: UserPlus,
  other: HelpCircle,
};

// Call type color mapping
const CALL_TYPE_COLORS = {
  job_outreach: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  voicemail: 'bg-muted/500/20 text-muted-foreground border-slate-500/30',
  submission_call: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  pre_screening: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  spotlight_call: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  interview_request: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  interview_prep: 'bg-green-500/20 text-green-400 border-green-500/30',
  interview_debrief: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  job_offer: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  counteroffer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  salary_negotiation: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
  onboarding: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  placement_checkin: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  timesheet: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  referral_check: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  client_introduction: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
  follow_up: 'bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30',
  other: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const PROCESS_TYPES = [
  { value: 'staffing', label: 'Staffing / Recruitment', description: 'Full recruitment lifecycle from outreach to placement' },
  { value: 'bpo', label: 'BPO (Coming Soon)', description: 'Business Process Outsourcing call types', disabled: true },
  { value: 'kpo', label: 'KPO (Coming Soon)', description: 'Knowledge Process Outsourcing call types', disabled: true },
];

function CallTypeCard({ callType, isExpanded, onToggle }) {
  const [parameters, setParameters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const Icon = CALL_TYPE_ICONS[callType.value] || HelpCircle;
  const colorClass = CALL_TYPE_COLORS[callType.value] || CALL_TYPE_COLORS.other;
  
  useEffect(() => {
    if (isExpanded && !parameters) {
      setIsLoading(true);
      getCallTypeParameters(callType.value, 'staffing')
        .then(res => setParameters(res.data))
        .catch(err => console.error('Failed to load parameters:', err))
        .finally(() => setIsLoading(false));
    }
  }, [isExpanded, callType.value, parameters]);
  
  return (
    <AccordionItem value={callType.value} className="border-slate-700">
      <AccordionTrigger className="hover:no-underline px-4 py-3">
        <div className="flex items-center gap-3 w-full">
          <div className={cn("p-2 rounded-lg border", colorClass)}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-medium text-white">{callType.name}</p>
            <p className="text-xs text-muted-foreground line-clamp-1">{callType.description}</p>
          </div>
          {parameters && (
            <Badge variant="outline" className="text-xs text-muted-foreground border-slate-600">
              {parameters.applicable_parameters?.length || 0} params
            </Badge>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading parameters...</div>
        ) : parameters ? (
          <div className="space-y-4">
            {/* Call Type Info */}
            <div className="p-3 rounded-lg bg-slate-700/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Typical Duration</p>
                  <p className="text-white font-medium">{parameters.call_type_info?.typical_duration}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Scoring Parameters</p>
                  <p className="text-white font-medium">{parameters.applicable_parameters?.length || 0} applicable</p>
                </div>
              </div>
              {parameters.call_type_info?.key_objectives && (
                <div className="mt-3">
                  <p className="text-muted-foreground text-sm mb-1">Key Objectives:</p>
                  <div className="flex flex-wrap gap-1">
                    {parameters.call_type_info.key_objectives.map((obj, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-slate-600/50 border-slate-500">
                        {obj}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Applicable Parameters */}
            <div>
              <p className="text-sm font-medium text-green-400 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Applicable Parameters ({parameters.applicable_parameters?.length || 0})
              </p>
              <div className="flex flex-wrap gap-2">
                {parameters.applicable_parameters?.map(param => (
                  <TooltipProvider key={param.value}>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                          {param.name}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs bg-slate-800">
                        <p className="text-xs">{param.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </div>
            
            {/* N/A Parameters */}
            {parameters.not_applicable_parameters?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Not Applicable ({parameters.not_applicable_parameters?.length || 0})
                </p>
                <div className="flex flex-wrap gap-2">
                  {parameters.not_applicable_parameters?.map(param => (
                    <Badge key={param.value} variant="outline" className="text-xs text-muted-foreground border-slate-600">
                      {param.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  );
}

export default function ProcessConfig() {
  const outletContext = useOutletContext() || {};
  const { theme = 'dark' } = outletContext;
  
  const [callTypes, setCallTypes] = useState([]);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedTypes, setExpandedTypes] = useState([]);
  
  // Form state
  const [processType, setProcessType] = useState('staffing');
  const [autoDetect, setAutoDetect] = useState(true);
  const [allowOverride, setAllowOverride] = useState(true);
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [typesRes, configRes] = await Promise.all([
          getCallTypes('staffing'),
          getTenantProcessConfig()
        ]);
        
        setCallTypes(typesRes.data.call_types || []);
        
        if (configRes.data) {
          setTenantConfig(configRes.data);
          setProcessType(configRes.data.process_type || 'staffing');
          setAutoDetect(configRes.data.auto_detect_call_type !== false);
          setAllowOverride(configRes.data.allow_call_type_override !== false);
        }
      } catch (error) {
        console.error('Failed to load process config:', error);
        toast.error('Failed to load process configuration');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateTenantProcessConfig({
        process_type: processType,
        auto_detect_call_type: autoDetect,
        allow_call_type_override: allowOverride
      });
      toast.success('Process configuration saved');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };
  
  const textClass = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedTextClass = theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500';
  const cardBgClass = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200';
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="process-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold flex items-center gap-3", textClass)}>
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Settings className="h-6 w-6 text-purple-400" />
            </div>
            Process Configuration
          </h1>
          <p className={cn("mt-1", mutedTextClass)}>
            Configure call type detection and scoring parameters for your tenant
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={isSaving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-config-btn"
        >
          {isSaving ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Configuration
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <Card className={cn(cardBgClass, "lg:col-span-1")}>
          <CardHeader>
            <CardTitle className={textClass}>Settings</CardTitle>
            <CardDescription>Configure how call types are detected and scored</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Process Type */}
            <div className="space-y-2">
              <Label className={textClass}>Process Type</Label>
              <Select value={processType} onValueChange={setProcessType}>
                <SelectTrigger className="bg-slate-700/50 border-slate-600" data-testid="process-type-select">
                  <SelectValue placeholder="Select process type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {PROCESS_TYPES.map(pt => (
                    <SelectItem key={pt.value} value={pt.value} disabled={pt.disabled}>
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4" />
                        <span>{pt.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {PROCESS_TYPES.find(pt => pt.value === processType)?.description}
              </p>
            </div>
            
            {/* Auto Detect */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className={textClass}>Auto-Detect Call Type</Label>
                <p className="text-xs text-muted-foreground">
                  AI will suggest call type from transcript
                </p>
              </div>
              <Switch 
                checked={autoDetect} 
                onCheckedChange={setAutoDetect}
                data-testid="auto-detect-switch"
              />
            </div>
            
            {/* Allow Override */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className={textClass}>Allow Manual Override</Label>
                <p className="text-xs text-muted-foreground">
                  Users can change AI-detected call type
                </p>
              </div>
              <Switch 
                checked={allowOverride} 
                onCheckedChange={setAllowOverride}
                data-testid="allow-override-switch"
              />
            </div>
            
            {/* Info Box */}
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-400 mt-0.5" />
                <div className="text-xs text-blue-300">
                  <p className="font-medium">Scope-Based Scoring</p>
                  <p className="mt-1 text-blue-400/80">
                    Each call type has specific scoring parameters. Parameters not applicable 
                    to a call type are marked as &quot;N/A&quot; and excluded from the average score.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Call Types Panel */}
        <Card className={cn(cardBgClass, "lg:col-span-2")}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={textClass}>
                  Call Types ({callTypes.length})
                </CardTitle>
                <CardDescription>
                  {processType === 'staffing' ? 'Staffing/Recruitment' : processType.toUpperCase()} call classifications
                </CardDescription>
              </div>
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                {processType.toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px] pr-4">
              <Accordion 
                type="multiple" 
                value={expandedTypes} 
                onValueChange={setExpandedTypes}
                className="space-y-2"
              >
                {callTypes.map(callType => (
                  <CallTypeCard 
                    key={callType.value} 
                    callType={callType}
                    isExpanded={expandedTypes.includes(callType.value)}
                  />
                ))}
              </Accordion>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
